import { useEffect, useState, useCallback, useRef } from 'react';
import { ApiClient, CreationStatus } from '../api/client';
import { useNavigate, useParams } from 'react-router-dom';
import { CreationItem } from './CreationItem';
import { UserInfo } from '../types';
import { useTranslation } from '../i18n';

interface ProjectsScreenProps {
  userInfo: UserInfo;
}

interface Creation {
  id: string;
  name?: string;
  created_at: string;
  updated_at: string;
  status: CreationStatus;
  uploaded_files: string[];
  result_files: string[];
  payment_id: string | null;
  pipelines: string[];
  strategy: {
    name: string;
  };
}

interface PaginatedCreations {
  items: Creation[];
  total: number;
  page: number;
  per_page: number;
}

export function ProjectsScreen({ userInfo }: ProjectsScreenProps) {
  const [creations, setCreations] = useState<PaginatedCreations | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { page } = useParams<{ page?: string }>();
  const { t } = useTranslation();

  // Debounce search input
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  // Focus search input when shown
  useEffect(() => {
    if (showSearch) {
      searchRef.current?.focus();
    }
  }, [showSearch]);

  const hasProcessingCreations = creations?.items.some(
    c => c.status === CreationStatus.IN_PROGRESS || c.status === CreationStatus.PENDING
  );

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchCreations = useCallback(
    async (page: number, query: string, replace = false) => {
      try {
        const response = await ApiClient.getCreations(page, 10, query);
        setCreations(prev => {
          if (!prev || replace) return response;
          if (page === 1) {
            // Replace only the first page items to avoid losing already loaded pages
            const rest = prev.items.slice(response.items.length);
            return { ...response, items: [...response.items, ...rest] };
          }
          return {
            ...response,
            items: [...(prev?.items ?? []), ...response.items],
          };
        });
      } catch {
        setError(t('errors.loadCreations'));
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [t]
  );

  // Reload loaded pages if any creations are still processing
  const refreshCurrentPages = useCallback(async () => {
    for (let p = 1; p <= currentPage; p++) {
      try {
        const response = await ApiClient.getCreations(p, 10, debouncedQuery);
        setCreations(prev => {
          if (!prev) return response;
          const start = (p - 1) * response.per_page;
          const updated = [...prev.items];
          response.items.forEach((item, idx) => {
            updated[start + idx] = item;
          });
          return { ...prev, items: updated, total: response.total };
        });
      } catch {
        // Ignore errors during background refresh
      }
    }
  }, [currentPage, debouncedQuery]);

  // Poll creations if any are in progress
  useEffect(() => {
    let interval: number | null = null;
    if (hasProcessingCreations) {
      interval = window.setInterval(() => {
        refreshCurrentPages();
      }, 10000);
    }
    return () => { if (interval) window.clearInterval(interval); };
  }, [hasProcessingCreations, refreshCurrentPages]);

  useEffect(() => {
    const initialPage = Math.max(parseInt(page ?? '1', 10), 1);
    if (creations && currentPage === initialPage) return;

    const loadPages = async () => {
      setIsLoading(true);
      setCreations(null);
      for (let p = 1; p <= initialPage; p++) {
        if (p > 1) setIsLoadingMore(true);
        await fetchCreations(p, debouncedQuery);
      }
      setCurrentPage(initialPage);
    };

    loadPages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCreations, page, debouncedQuery]);

  const handleCreationClick = (id: string) => {
    navigate(`/creation/${id}`);
  };

  const handleSearch = useCallback(
    async (query: string) => {
      setIsLoading(true);
      setCreations(null);
      await fetchCreations(1, query, true);
      setCurrentPage(1);
      navigate('/projects/1');
      setDebouncedQuery(query);
    },
    [fetchCreations, navigate]
  );
  const applySearch = async () => {
    await handleSearch(searchQuery.trim());
  };

  const clearSearch = async () => {
    setSearchQuery('');
    setDebouncedQuery('');
    setShowSearch(false);
    setCurrentPage(1);
    navigate('/projects/1');
    await fetchCreations(1, '');
  };

  const handleLoadMore = async () => {
    if (!creations || isLoadingMore || currentPage * creations.per_page >= creations.total) return;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    navigate(`/projects/${nextPage}`);
    await fetchCreations(nextPage, debouncedQuery);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-theme-button border-t-transparent rounded-full animate-spin" />
          <span className="text-theme-hint animate-pulse">{t('home.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-500/5 dark:bg-red-500/10 border border-red-500/10 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Pro Banner */}
      <div className="fixed top-0 left-0 right-0 z-[100] shadow-[0_4px_12px_-6px_rgba(0,0,0,0.1)]" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/subscription')}
            >
              <span className="bg-black text-white dark:bg-white dark:text-black px-2 py-0.5 rounded-md text-sm font-semibold">{t('homeScreen.proBannerLabel')}</span>
              <span className="text-theme-text/90">{t('homeScreen.proBannerDescription')}</span>
            </div>
            <button 
              onClick={() => navigate('/balance')}
              className="flex items-center gap-1.5 text-theme-hint hover:text-theme-text transition-colors p-2 -mr-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
            >
              <span className="text-base">✨</span>
              <span className="font-medium">{userInfo.balance}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Projects Section */}
      <div className="pt-[72px] px-4 pb-24">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-theme-text/90">{t('navigation.projects')}</h2>
          <button
            onClick={() => setShowSearch(v => !v)}
            className="p-2 text-theme-hint hover:text-theme-text transition-colors"
          >
            <span className="material-symbols-outlined">search</span>
          </button>
        </div>
        {showSearch && (
          <div className="mb-4 flex items-center gap-2">
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={e => {
                const value = e.target.value;
                setSearchQuery(value);
                if (value === '') {
                  handleSearch('');
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch(e.currentTarget.value.trim());
                }
              }}
              placeholder={t('projectsScreen.searchPlaceholder')}
              className="flex-grow py-2 px-3 rounded-lg border border-black/10 dark:border-white/10 bg-transparent focus:outline-none"
            />
            <button
              onClick={applySearch}
              className="p-2 rounded-lg bg-theme-button text-white hover:bg-theme-button/90 transition-colors shrink-0 flex items-center justify-center"
            >
              <span className="material-symbols-outlined">search</span>
              <span className="sr-only">{t('projectsScreen.searchButton')}</span>
            </button>
            <button
              onClick={clearSearch}
              className="p-2 rounded-lg bg-black/[0.05] dark:bg-white/[0.05] hover:bg-black/[0.1] dark:hover:bg-white/[0.1] transition-colors shrink-0 flex items-center justify-center"
            >
              <span className="material-symbols-outlined">close</span>
              <span className="sr-only">{t('projectsScreen.clearButton')}</span>
            </button>
          </div>
        )}
        {!creations?.items.length ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-24 h-24 border-2 border-theme-hint/20 rounded-xl flex items-center justify-center mb-4">
              <span className="material-symbols-outlined text-4xl text-theme-hint/50">movie</span>
            </div>
            <p className="text-theme-hint/70 text-base mb-8 max-w-sm">
              {t('home.emptyState')}
            </p>
          </div>
        ) : (
          <div>
            <div className="space-y-2">
              {creations.items.map(creation => (
                <CreationItem
                  key={creation.id}
                  id={creation.id}
                  status={creation.status}
                  strategyName={creation.strategy.name}
                  name={creation.name}
                  createdAt={creation.created_at}
                  onClick={handleCreationClick}
                  onDeleted={() => fetchCreations(1, debouncedQuery)}
                />
              ))}
            </div>
            {isLoadingMore ? (
              <button disabled className="w-full mt-6 py-3 px-4 rounded-xl bg-black/[0.03] dark:bg-[#1c2834] shadow-[0_2px_8px_-2px_rgba(0,0,0,0.08)] opacity-50 cursor-not-allowed dark:border dark:border-white/[0.06]">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  <span>{t('projectsScreen.loadingMore')}</span>
                </div>
              </button>
            ) : creations && currentPage * creations.per_page < creations.total ? (
              <button onClick={handleLoadMore} className="w-full mt-6 py-3 px-4 rounded-xl bg-black/[0.03] dark:bg-[#1c2834] hover:bg-theme-button/[0.08] dark:hover:bg-[#243242] text-theme-text/80 hover:text-theme-text/90 transition-all duration-200 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_16px_-6px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 dark:border dark:border-white/[0.06]">
                <span>{t('projectsScreen.loadMoreButton')}</span>
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
} 